import { useState, type ReactNode } from 'react';
import { Bell, ChevronDown } from 'lucide-react';

export type AttentionItem = {
  key: string;
  count: number;
  /** Singular / plural label, e.g. ['unsent report', 'unsent reports'] */
  label: [string, string];
  tone: 'amber' | 'sky';
};

export function summaryLine(items: AttentionItem[]): string {
  return items
    .filter((i) => i.count > 0)
    .map((i) => `${i.count} ${i.count === 1 ? i.label[0] : i.label[1]}`)
    .join(' · ');
}

/**
 * One "Needs attention" block for the Lite dashboard (Chris 2026-09-16): the
 * unsubmitted / needs-a-report / unsent-reports cards stacked into a page of
 * amber and blue. Folded by default it is one line with the counts; tapping
 * the header opens the three lists exactly as they were. The choice sticks per
 * device. Nothing renders when every count is zero.
 */
export function AttentionPanel({
  items,
  children,
  keepOpen = false,
}: {
  items: AttentionItem[];
  children: ReactNode;
  // Jim 2026-10-08: a just-sent receipt must not vanish with the count.
  // While the queue still has rows to show, the quiet bar and the lists
  // stay on screen together.
  keepOpen?: boolean;
}) {
  // Jim 2026-10-08: "not have it open when you first go to dashboard." It
  // starts folded every time; the counts in the bar say what is inside.
  const [open, setOpen] = useState(false);
  const total = items.reduce((n, i) => n + i.count, 0);
  // JIM-1005-06 (Jim 2026-10-05: "it should be there even if I didn't need
  // it"): with nothing waiting the bar still shows, quiet, so the inspector
  // always knows where unfinished and unsent work would be.
  // Jim 2026-10-08: the body keeps the same place in the tree whether the bar
  // is quiet or counted, so a receipt the queue is still showing survives the
  // count reaching zero (a different wrapper would remount the queue and lose
  // it). `keepOpen` says the queue still has rows to show.
  const quietHeader = (
    <div key="header" className="px-4 py-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
        <Bell className="w-5 h-5 text-slate-400" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">
          Needs attention
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Nothing waiting on you.
        </p>
      </div>
    </div>
  );
  const showBody = total === 0 ? keepOpen : open;

  const toggle = () => {
    const next = !open;
    setOpen(next);
  };

  return (
    <section
      className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-soft overflow-hidden"
      data-attention-open={total === 0 ? 'empty' : open ? 'true' : 'false'}
    >
      {total === 0 ? (
        quietHeader
      ) : (
        <button
          key="header"
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="lite-attention-body"
          className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
        >
          <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              Needs attention
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-amber-600 text-white text-xs font-bold">
                {total}
              </span>
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 flex flex-wrap gap-x-1.5">
              {items
                .filter((i) => i.count > 0)
                .map((i, idx) => (
                  <span
                    key={i.key}
                    className="inline-flex items-center gap-1.5"
                  >
                    {idx > 0 && (
                      <span className="text-slate-400" aria-hidden="true">
                        ·
                      </span>
                    )}
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        i.tone === 'amber' ? 'bg-amber-500' : 'bg-sky-500'
                      }`}
                      aria-hidden="true"
                    />
                    {i.count} {i.count === 1 ? i.label[0] : i.label[1]}
                  </span>
                ))}
            </p>
          </div>
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1 flex-shrink-0">
            {open ? 'Hide' : 'Show'}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
            />
          </span>
        </button>
      )}
      {showBody && (
        <div
          key="body"
          id="lite-attention-body"
          className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-3 bg-slate-50 dark:bg-slate-900/40"
        >
          {children}
        </div>
      )}
    </section>
  );
}
